import { Body } from '@assembly-js/design-system';
import clsx from 'clsx';
import { useCallback, useState } from 'react';

import { useNoteModal } from 'src/features/notes/NoteModal/noteModalContext';
import {
  Note,
  NOTES_LIST_LIMIT,
  useCreateNoteMutation,
  useDeleteNoteMutation,
  useListNotesQuery,
  useUpdateNoteMutation,
} from 'src/services/api/notesApi';

import { NotesList } from './notes-list/NotesList';

// Hold the "saving" shimmer for at least this long on update so it's visibly
// rendered even when the server responds in a few ms.
const MIN_UPDATE_SHIMMER_MS = 600;

const NotesListContent = ({
  notes,
  onClick,
  pendingUpdateIds,
}: {
  notes: Note[];
  onClick: (note: Note) => void;
  pendingUpdateIds: ReadonlySet<string>;
}) => {
  if (!notes.length) {
    return (
      <div className="w-full flex justify-center items-center py-3">
        <Body className="text-secondary">Click + to add a note</Body>
      </div>
    );
  }
  return (
    <NotesList
      notes={notes}
      onClick={onClick}
      pendingUpdateIds={pendingUpdateIds}
    />
  );
};
export const Notes = ({
  entityType,
  entityId,
}: {
  entityType: 'client' | 'company';
  entityId: string;
}) => {
  const { data } = useListNotesQuery({
    entityType,
    entityId,
    limit: NOTES_LIST_LIMIT,
  });
  const [createNote] = useCreateNoteMutation();
  const [updateNote] = useUpdateNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();
  const [pendingUpdateIds, setPendingUpdateIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const addPendingId = useCallback((id: string) => {
    setPendingUpdateIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  const removePendingId = useCallback((id: string) => {
    setPendingUpdateIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const openNoteModal = useNoteModal({
    onDelete: async (note) => {
      await deleteNote({
        id: note.id,
        entityType: note.entityType,
        entityId: note.entityId,
      });
    },
    onSave: (title, content, note) => {
      if (note) {
        const { id } = note;
        addPendingId(id);
        const startedAt = Date.now();
        // NOTE: the entityType stored on the note is the server's canonical
        // uppercase form (e.g. 'COMPANY'), but the listNotes cache is keyed
        // by the component's lowercase prop. Pass the lowercase prop so
        // updateNote.onQueryStarted can find the cache entry to patch.
        void updateNote({
          id,
          title,
          content,
          entityType,
          entityId,
        })
          .unwrap()
          .catch(() => {
            /* error toast handled inside the mutation */
          })
          .finally(() => {
            const elapsed = Date.now() - startedAt;
            const remaining = Math.max(0, MIN_UPDATE_SHIMMER_MS - elapsed);
            setTimeout(() => removePendingId(id), remaining);
          });
      } else {
        createNote({
          entityType,
          entityId,
          title,
          content,
        });
      }
    },
  });

  return (
    <div
      className={clsx('w-full py-[18px]', {
        ' h-[123px] box-border': !data?.data.length,
      })}
    >
      <NotesListContent
        notes={data?.data ?? []}
        onClick={(note) => openNoteModal(note)}
        pendingUpdateIds={pendingUpdateIds}
      />
    </div>
  );
};
